// @flow
import * as React from 'react';

import { setAutoMessageStatus } from 'admin/actions/AutoMessageActions';
import AdminNoticeBanner from 'admin/components/AdminNoticeBanner';
import Button from 'components/Button.jsx';
import Container from 'components/Container.jsx';
import Heading from 'components/Heading.jsx';
import List from 'components/List.jsx';
import ListItem from 'components/ListItem.jsx';
import MediaObject from 'components/MediaObject.jsx';
import OrgAvatar from 'organizations/components/OrgAvatar.jsx';
import Row from 'components/Row.jsx';
import Text from 'components/Text.jsx';
import ToastActions from 'actions/ToastActions.js';
import View from 'components/View.jsx';
import WizardPageLayout from 'components/WizardPageLayout';

import type {
  GetAutoMessageWithoutSends_autoMessaging_autoMessages_autoMessages_messageContent,
  GetAutoMessageWithoutSends_autoMessaging_autoMessages_autoMessages_sender,
} from 'admin/components/AdminAutoMessages/queries/__generated__/GetAutoMessageWithoutSends';
import type { AdminNewAutoMessageOrgs_organizations } from './queries/__generated__/AdminNewAutoMessageOrgs';
import type { AutoMessageTabKey } from './';

import './styles/AdminNewAutoMessageReview.scss';

type Props = {
  sender: ?GetAutoMessageWithoutSends_autoMessaging_autoMessages_autoMessages_sender,
  messageContent: ?GetAutoMessageWithoutSends_autoMessaging_autoMessages_autoMessages_messageContent,
  orgUuid: string,
  messageUuid: string,
  messageName: string,
  formatCSVComplete: boolean,
  syncDataComplete: boolean,
  composeComplete: boolean,
  onSave: (fields: SaveAutoMessageInput, onSuccess: Function) => void,
  onBack: () => void,
  onClose: () => void,
  onJump: (key: AutoMessageTabKey) => void,
  selectedOrg: AdminNewAutoMessageOrgs_organizations,
  isActivatable: boolean,
  status: AutoMessageStatus,
};

function BorderedPreviewBox(props: { children: any }) {
  return (
    <Container size="small" align="left">
      <div className="AdminNewAutoMessageReview-box">
        <Text align="center" element="div">
          <Text color="wolf" size="xsmall">
            This is a preview
          </Text>
          <div className="AdminNewAutoMessageReview-box-preview">
            {props.children || (
              <Text italic color="eclipse">
                Compose your message and select a sender in order to see a preview
              </Text>
            )}
          </div>
        </Text>
      </div>
    </Container>
  );
}

function ErrorFormat(props: {
  title: string,
  details: string[],
  tabKey: AutoMessageTabKey,
  onClickCompleteButton: (key: AutoMessageTabKey) => void,
}) {
  return (
    <MediaObject figure="right">
      <div>
        <Heading level="6">{props.title}</Heading>
        <List marker="number" size="medium">
          {props.details.map(detail => (
            <ListItem key={detail}>{detail}</ListItem>
          ))}
        </List>
      </div>
      <Button
        size="xxsmall"
        color="lava"
        level="secondary"
        onClick={() => props.onClickCompleteButton(props.tabKey)}>
        Complete
      </Button>
    </MediaObject>
  );
}

class AdminNewAutoMessageReview extends React.Component<Props, *> {
  getActionButtons = () => {
    const { status, isActivatable, messageName, messageUuid, orgUuid, onClose } = this.props;
    function SaveButton(props: { label: string }) {
      return (
        <Button
          level="secondary"
          data-test="save-draft-button"
          onClick={() => {
            onClose();
            ToastActions.showSuccessToast(`${messageName} was successfully saved.`);
          }}>
          {props.label}
        </Button>
      );
    }
    function DeactivateButton() {
      return (
        <Button
          level="secondary"
          data-test="deactivate-button"
          onClick={() =>
            setAutoMessageStatus(messageUuid, 'inactive', orgUuid, messageName, onClose)
          }>
          Deactivate
        </Button>
      );
    }
    function ActivateButton(props: { label: string }) {
      return (
        <Button
          data-test="activate-button"
          disabled={!isActivatable}
          onClick={() =>
            setAutoMessageStatus(messageUuid, 'active', orgUuid, messageName, onClose)
          }>
          {props.label}
        </Button>
      );
    }

    if (status === 'draft') {
      return [<SaveButton label="Save draft" />, <ActivateButton label="Activate" />];
    }

    if (status === 'inactive') {
      return [<SaveButton label="Keep inactive" />, <ActivateButton label="Activate" />];
    }

    if (status === 'active') {
      return [<DeactivateButton />, <ActivateButton label="Keep active" />];
    }

    console.warn({ status });
    console.error('AdminNewAutoMessageReview - invalid auto message status');
    return [];
  };

  renderIncompleteFields = () => {
    const { selectedOrg, formatCSVComplete, syncDataComplete, composeComplete } = this.props;

    const formatCSVError = (
      <ErrorFormat
        title="Export CSV Report"
        details={['Enter a CSV filename']}
        tabKey="format-csv"
        onClickCompleteButton={this.props.onJump}
      />
    );
    const syncDataError = (
      <ErrorFormat
        title="Transfer Files to Remind"
        details={['Select a set of SFTP credentials']}
        tabKey="sync-data"
        onClickCompleteButton={this.props.onJump}
      />
    );
    const composeMessageErrorDetails = ['Select recipients', 'Type your message'];
    // we only need this error detail for schools; users
    // don't need to manually select a sender for a district
    if (selectedOrg.type === 'School') {
      composeMessageErrorDetails.unshift('Assign an individual administrator as sender');
    }
    const composeMessageError = (
      <ErrorFormat
        title="Compose"
        details={composeMessageErrorDetails}
        tabKey="compose"
        onClickCompleteButton={this.props.onJump}
      />
    );

    return (
      <Row>
        <Container size="medium" flush align="left">
          <AdminNoticeBanner
            alwaysShowDetails
            glyph="explanation-circle"
            color="lava"
            actionElement={null}
            details={
              <List size="large">
                {!formatCSVComplete && formatCSVError}
                {!syncDataComplete && syncDataError}
                {!composeComplete && composeMessageError}
              </List>
            }>
            Please complete the following before setting your auto message live.
          </AdminNoticeBanner>
        </Container>
      </Row>
    );
  };

  renderMockAnnouncement = (
    selectedOrg: AdminNewAutoMessageOrgs_organizations,
    messageContent: GetAutoMessageWithoutSends_autoMessaging_autoMessages_autoMessages_messageContent
  ) => {
    return (
      <Text align="left" element="div">
        <div className="AnnouncementMessageItem AnnouncementMessageItem-other">
          <div className="AnnouncementMessageItem-column">
            <div className="AnnouncementMessageItem-dropletChubble">
              <div className="AnnouncementMessageItem-chubble">
                <MediaObject center gutter="xxsmall" className="AnnouncementMessageItem-head">
                  <OrgAvatar size="small" className="AnnouncementMessageItem-announcementBadge" />
                  <div className="AnnouncementMessageItem-head-classInfo">
                    <Text weight="semibold" color="pitch" truncate>
                      {selectedOrg.name}
                    </Text>
                    <Text color="thunder" truncate>
                      Shared with you
                    </Text>
                  </div>
                </MediaObject>
                <div className="AnnouncementMessageItem-content">
                  <div className="MessageItemBody-body">{messageContent.body}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Text>
    );
  };

  render() {
    const { sender, selectedOrg, messageContent, onBack } = this.props;

    return (
      <WizardPageLayout
        actionButtons={this.getActionButtons()}
        backButton={
          <Button data-test="back-button" level="secondary" onClick={onBack}>
            Back
          </Button>
        }>
        <View>
          <Row size="xlarge">
            <Container flush size="large">
              <Text align="left" lineHeight="large">
                <Row size="small">
                  <Heading level="2">
                    <Text weight="semibold">Review auto message</Text>
                  </Heading>
                  <Text>
                    The recipient will receive the following message in a conversation
                    {sender && ` with ${sender.name || 'the sender'}`}.
                  </Text>
                </Row>
                {this.renderIncompleteFields()}
                <Container align="left" size="medium">
                  <Heading level="3" levelStyle="4">
                    Text message
                  </Heading>
                  <Row>
                    <BorderedPreviewBox>
                      {messageContent && sender ? (
                        <div className="AdminNewAutoMessageReview-chatBubble">
                          {`${sender.name || 'Sender name'}: ${messageContent.body}`}
                        </div>
                      ) : null}
                    </BorderedPreviewBox>
                  </Row>
                  <Heading level="3" levelStyle="4">
                    Mobile app
                  </Heading>
                  <Row>
                    <BorderedPreviewBox>
                      {messageContent && selectedOrg
                        ? this.renderMockAnnouncement(selectedOrg, messageContent)
                        : null}
                    </BorderedPreviewBox>
                  </Row>
                  <Heading level="3" levelStyle="4">
                    Do you want to test your auto message before activating it?
                  </Heading>
                  <Text element="p">
                    You can test inactive auto messages (both drafts and deactivated messages)
                    before you activate them. You can find the test details, including send time and
                    recipients, under Auto messages. Messages will not be sent until you activate
                    this auto message.
                  </Text>
                </Container>
              </Text>
            </Container>
          </Row>
        </View>
      </WizardPageLayout>
    );
  }
}

export default AdminNewAutoMessageReview;
